// components/TeamManagement.tsx
import { useState } from "react";
import { Card, Table, Tag, Button, Modal, Form, Input, Select, Avatar, Space, Tooltip, message, Popconfirm } from "antd";
import { 
  UserAddOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  TeamOutlined,
  MailOutlined,
  PhoneOutlined,
  ExportOutlined
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { DashboardMetrics, Team, TeamPermission, TeamUser } from "../types";
import * as XLSX from "xlsx";

const { Option } = Select;

interface TeamManagementProps {
  data: DashboardMetrics;
}

interface TeamMember {
  key: string;
  id: string;
  name: string;
  email: string;
  team: string;
  role: string;
  permissions: string[];
  status: 'active' | 'inactive';
  phone?: string;
  joinDate: string;
}

export const TeamManagement = ({ data }: TeamManagementProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  // Transform real data from backend
  const allTeamMembers: TeamMember[] = data.team_structure?.teams?.flatMap(team => 
    team.users?.map(user => ({
      key: user.id,
      id: user.id,
      name: user.name,
      email: user.email,
      team: team.name,
      role: getRoleFromPermissions(user.id, data.team_structure.permissions),
      permissions: getPermissionsForUser(user.id, data.team_structure.permissions),
      status: 'active',
      joinDate: new Date().toISOString().split('T')[0] // Would come from real data
    })) || []
  ) || [];

  const filteredMembers = selectedTeam === 'all' 
    ? allTeamMembers 
    : allTeamMembers.filter(member => member.team === selectedTeam);

  const teams = data.team_structure?.teams || [];

  const handleExportTeam = () => {
    try {
      const exportData = filteredMembers.map(member => ({
        'Name': member.name,
        'Email': member.email,
        'Team': member.team,
        'Role': member.role,
        'Permissions': member.permissions.join(', '),
        'Status': member.status,
        'Join Date': member.joinDate
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Team Members");
      XLSX.writeFile(wb, `Team_Members_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      message.success('Team data exported successfully');
    } catch (error) {
      message.error('Failed to export team data');
    }
  };

  const handleAddMember = () => {
    form.resetFields();
    setEditingMember(null);
    setIsModalVisible(true);
  };

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    form.setFieldsValue({
      name: member.name,
      email: member.email,
      team: teams.find(t => t.name === member.team)?.id,
      role: member.role.toLowerCase(),
      permissions: member.permissions
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (member: TeamMember) => {
    setLoading(true);
    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      message.success(`${member.name} removed from team`);
      // In real app, you would refetch data here
    } catch (error) {
      message.error('Failed to remove team member');
    } finally {
      setLoading(false);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (editingMember) {
        message.success(`${values.name}'s details updated successfully`);
      } else {
        message.success(`${values.name} added to team successfully`);
      }
      
      form.resetFields();
      setIsModalVisible(false);
      setEditingMember(null);
      
      // In real app, you would refetch data here
      // await refetch();
      
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<TeamMember> = [
    {
      title: 'Member',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (text, record) => (
        <Space>
          <Avatar 
            size="large" 
            style={{ 
              backgroundColor: stringToColor(record.name),
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {text.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div className="font-semibold">{text}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <MailOutlined className="text-xs" />
              {record.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      render: (team) => (
        <Tag 
          color={getTeamColor(team)} 
          className="px-3 py-1 rounded-full font-medium"
        >
          {team}
        </Tag>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag 
          color={role === 'Admin' ? 'red' : role === 'Manager' ? 'orange' : 'green'}
          className="px-3 py-1 rounded-full"
        >
          {role}
        </Tag>
      ),
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 200,
      render: (permissions: string[]) => (
        <div className="flex flex-wrap gap-1">
          {permissions.slice(0, 2).map((perm, index) => (
            <Tooltip key={index} title={perm}>
              <Tag 
                color="geekblue" 
                className="cursor-help text-xs px-2 py-0.5"
              >
                {perm.replace('_', ' ')}
              </Tag>
            </Tooltip>
          ))}
          {permissions.length > 2 && (
            <Tooltip title={permissions.slice(2).join(', ')}>
              <Tag className="cursor-help">+{permissions.length - 2} more</Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Join Date',
      dataIndex: 'joinDate',
      key: 'joinDate',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag 
          color={status === 'active' ? 'success' : 'error'} 
          className="px-3 py-1 rounded-full uppercase"
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
            className="text-blue-500 hover:text-blue-700"
          />
          <Popconfirm
            title="Remove Team Member"
            description={`Are you sure you want to remove ${record.name} from the team?`}
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />}
              loading={loading}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {teams.map(team => (
          <Card key={team.id} className="text-center hover:shadow-md transition-shadow">
            <TeamOutlined className="text-3xl text-blue-500 mb-3 mx-auto" />
            <div className="font-bold text-2xl mb-1">{team.users?.length || 0}</div>
            <div className="text-sm text-gray-600 font-medium">{team.name}</div>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <Card className="shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Select
              value={selectedTeam}
              onChange={setSelectedTeam}
              className="w-48"
              placeholder="Filter by team"
              allowClear
              size="middle"
            >
              <Option value="all">All Teams ({allTeamMembers.length})</Option>
              {teams.map(team => (
                <Option key={team.id} value={team.name}>
                  {team.name} ({team.users?.length || 0})
                </Option>
              ))}
            </Select>
            <div className="text-sm text-gray-600">
              Showing {filteredMembers.length} of {allTeamMembers.length} members
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              icon={<ExportOutlined />}
              onClick={handleExportTeam}
              size="middle"
            >
              Export
            </Button>
            <Button 
              type="primary" 
              icon={<UserAddOutlined />}
              onClick={handleAddMember}
              size="middle"
            >
              Add Team Member
            </Button>
          </div>
        </div>
      </Card>

      {/* Members Table */}
      <Card className="shadow-lg">
        <Table
          columns={columns}
          dataSource={filteredMembers}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} members`,
          }}
          scroll={{ x: 1200 }}
          loading={loading}
          rowClassName="hover:bg-gray-50"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingMember ? 'Edit Team Member' : 'Add Team Member'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
          setEditingMember(null);
        }}
        width={600}
        confirmLoading={loading}
        okText={editingMember ? 'Update' : 'Add'}
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-6"
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Full Name"
              name="name"
              rules={[{ required: true, message: 'Please enter name' }]}
            >
              <Input placeholder="Enter full name" size="large" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter valid email' }
              ]}
            >
              <Input placeholder="Enter email" size="large" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Team"
              name="team"
              rules={[{ required: true, message: 'Please select team' }]}
            >
              <Select placeholder="Select team" size="large">
                {teams.map(team => (
                  <Option key={team.id} value={team.id}>
                    {team.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Role"
              name="role"
              rules={[{ required: true, message: 'Please select role' }]}
            >
              <Select placeholder="Select role" size="large">
                <Option value="admin">Admin</Option>
                <Option value="manager">Manager</Option>
                <Option value="recruiter">Recruiter</Option>
                <Option value="coordinator">Coordinator</Option>
                <Option value="viewer">Viewer</Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            label="Permissions"
            name="permissions"
            extra="Select all permissions this member should have"
          >
            <Select
              mode="multiple"
              placeholder="Select permissions"
              allowClear
              size="large"
            >
              <Option value="view_jobs">View Jobs</Option>
              <Option value="create_jobs">Create Jobs</Option>
              <Option value="edit_jobs">Edit Jobs</Option>
              <Option value="delete_jobs">Delete Jobs</Option>
              <Option value="view_talents">View Talents</Option>
              <Option value="contact_talents">Contact Talents</Option>
              <Option value="schedule_interviews">Schedule Interviews</Option>
              <Option value="manage_team">Manage Team</Option>
              <Option value="view_analytics">View Analytics</Option>
              <Option value="admin">Full Admin Access</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// Helper functions
function getRoleFromPermissions(userId: string, permissions: TeamPermission[]): string {
  const userPermission = permissions.find(p => p.user_id === userId);
  if (!userPermission) return 'Member';
  
  const perms = userPermission.permissions;
  if (perms.includes('admin')) return 'Admin';
  if (perms.includes('manage_team')) return 'Manager';
  if (perms.includes('create_jobs') || perms.includes('edit_jobs')) return 'Recruiter';
  return 'Member';
}

function getPermissionsForUser(userId: string, permissions: TeamPermission[]): string[] {
  const userPermission = permissions.find(p => p.user_id === userId);
  return userPermission?.permissions || ['view_jobs'];
}

function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    '#3B82F6', // Blue
    '#10B981', // Emerald
    '#8B5CF6', // Violet
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#06B6D4', // Cyan
  ];
  return colors[Math.abs(hash) % colors.length];
}

function getTeamColor(teamName: string): string {
  const colors: Record<string, string> = {
    'Recruitment': 'blue',
    'HR': 'green',
    'Operations': 'purple',
    'Management': 'orange',
    'Marketing': 'pink',
    'Sales': 'cyan',
    'Default': 'gray'
  };
  
  for (const [key, color] of Object.entries(colors)) {
    if (teamName.includes(key)) return color;
  }
  return colors['Default'];
}